import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { billingApi, formatCents, type ReferralSummary } from '@/services/billingApi';

/**
 * Recruiter dashboard: personal referral code, referral stats, earnings, and
 * withdrawal. Self-contained — fetches its own summary. Renders nothing for
 * non-recruiters. Used by both the Account tab and the Billing page.
 */
export function RecruiterPanel(): JSX.Element | null {
  const { t } = useTranslation();
  const [referral, setReferral] = useState<ReferralSummary | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await billingApi.getReferral();
      setReferral(r);
    } catch {
      setReferral(null);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function run(key: string, fn: () => Promise<void>): Promise<void> {
    setBusy(key);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('billing.referrals.actionFailed'));
    } finally {
      setBusy(null);
    }
  }

  // Hide entirely for non-recruiters (and while the first load is in flight).
  if (!loaded) return <p className="muted">{t('billing.referrals.loading')}</p>;
  if (!referral || !referral.isRecruiter) return null;

  return (
    <section className="admin-card" data-testid="recruiter-panel">
      <h2 className="admin-card__title">{t('billing.referrals.title')}</h2>
      <p className="muted">
        {t('billing.referrals.earnLine', { percent: Math.round(referral.commissionRate * 100) })}
      </p>

      <div className="field">
        <label>{t('billing.referrals.codeLabel')}</label>
        {referral.referralCode ? (
          <div className="referral-code-row">
            <code className="referral-code">{referral.referralCode}</code>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                if (referral.referralCode) {
                  void navigator.clipboard?.writeText(referral.referralCode);
                  setCopied(true);
                  window.setTimeout(() => setCopied(false), 1500);
                }
              }}
            >
              {copied ? t('billing.referrals.copied') : t('billing.referrals.copy')}
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="btn"
            disabled={busy === 'mint'}
            onClick={() => void run('mint', async () => { await billingApi.mintReferralCode(); await refresh(); })}
          >
            {busy === 'mint' ? t('billing.referrals.creating') : t('billing.referrals.createCode')}
          </button>
        )}
      </div>

      <div className="stat-grid">
        <div className="stat">
          <div className="stat__value">{referral.recruitedCount}</div>
          <div className="stat__label">{t('billing.referrals.peopleReferred')}</div>
        </div>
        <div className="stat">
          <div className="stat__value">{formatCents(referral.earnedCents)}</div>
          <div className="stat__label">{t('billing.referrals.totalEarned')}</div>
        </div>
        <div className="stat">
          <div className="stat__value">{formatCents(referral.balanceCents)}</div>
          <div className="stat__label">{t('billing.referrals.availableBalance')}</div>
        </div>
      </div>

      <button
        type="button"
        className="btn"
        disabled={!referral.canWithdraw || busy === 'withdraw'}
        onClick={() => void run('withdraw', async () => { await billingApi.requestWithdrawal(); await refresh(); })}
        style={{ marginTop: 12 }}
      >
        {busy === 'withdraw' ? t('billing.referrals.requesting') : t('billing.referrals.withdraw', { min: formatCents(referral.withdrawMinCents) })}
      </button>
      {!referral.canWithdraw && (
        <p className="muted" style={{ marginTop: 8 }}>
          {t('billing.referrals.reachMin', { min: formatCents(referral.withdrawMinCents) })}
        </p>
      )}
      {error && <p className="error" role="alert" style={{ marginTop: 8 }}>{error}</p>}
    </section>
  );
}
